/**
 * `<PrepSplitPanel>` — six-section split panel for the Interview Prep
 * tab `loaded` phase (Batch 30j-4).
 *
 * Pure render. Composes existing primitives:
 *   - `<SplitPanel>` (foundations §9.15)
 *   - `<EvaluationMarkdown>` (Batch 30j-3 — HARD-2 reuse, no fork)
 *   - `<QuestionList>` (Batch 30j-4 — only the Questions section)
 *
 * Spec: `frontend/docs/30j_4_design.md` §3.3 +
 *       `frontend/docs/30j_4_architect.md` §5.
 *
 * Six fixed sections in canonical display order (Background / Strengths /
 * Probe / Red Flags / Strategy / Questions). Section list is structural,
 * NOT data-derived — empty bodies still show in the nav (recruiter sees
 * "Red Flags" with 0 bullets and knows the AI didn't surface any).
 *
 * Active section is `useState`, NOT URL state (HoD-30j4-11 / S-30j4-18).
 * Resets to `background` whenever the parsed object reference changes
 * (regenerate path).
 *
 * HARD-9 (HoD-30j4-6): Section colors drive the SplitPanel's detail label
 * and the active nav-item border-left/icon — NEVER body markdown.
 * `<EvaluationMarkdown>` body stays `text-text-secondary`.
 */
import { useEffect, useState, type JSX } from "react";
import {
  AlertTriangle,
  MessageSquare,
  Search,
  Sparkles,
  Target,
  User,
} from "lucide-react";

import { SplitPanel } from "@/components/common/SplitPanel";

import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import { QuestionList } from "@/components/candidate-page/QuestionList";
import type { ParsedPrep, PrepSectionKey } from "@/lib/parsePrep";

interface PrepNavItem {
  key: PrepSectionKey;
  label: string;
  icon: JSX.Element;
  color: string;
}

/**
 * Six-section nav definition. Order is the canonical DISPLAY order
 * (HoD §3.3) — independent of parser source order. Locked to a const
 * so referential identity is stable across re-renders.
 */
const PREP_NAV_ITEMS: ReadonlyArray<PrepNavItem> = [
  {
    key: "background",
    label: "Background",
    icon: <User size={14} />,
    color: "var(--section-overview)",
  },
  {
    key: "strengths",
    label: "Strengths",
    icon: <Sparkles size={14} />,
    color: "var(--section-strengths)",
  },
  {
    key: "probe",
    label: "Areas to Probe",
    icon: <Search size={14} />,
    color: "var(--section-gaps)",
  },
  {
    key: "redFlags",
    label: "Red Flags",
    icon: <AlertTriangle size={14} />,
    // AR-10: Red Flags maps to verdict-weak-fg (red), divergent from the
    // strict gaps→amber chain. The danger semantic is the right signal
    // here; ratified by Architect §5.4.
    color: "var(--verdict-weak-fg)",
  },
  {
    key: "strategy",
    label: "Strategy",
    icon: <Target size={14} />,
    color: "var(--section-tenure)",
  },
  {
    key: "questions",
    label: "Questions",
    icon: <MessageSquare size={14} />,
    color: "var(--section-recommendation)",
  },
];

interface PrepSplitPanelProps {
  parsed: ParsedPrep;
}

export function PrepSplitPanel({
  parsed,
}: PrepSplitPanelProps): JSX.Element {
  const [activeKey, setActiveKey] = useState<PrepSectionKey>("background");

  // Reset to first nav item whenever the parsed object reference changes
  // (regenerate path emits a fresh ParsedPrep). S-30j4-21.
  useEffect(() => {
    setActiveKey("background");
  }, [parsed]);

  const activeItem =
    PREP_NAV_ITEMS.find((n) => n.key === activeKey) ?? PREP_NAV_ITEMS[0]!;

  return (
    <div className="px-content-px py-5" data-testid="prep-split-panel">
      <SplitPanel
        navItems={PREP_NAV_ITEMS.map((n) => ({
          key: n.key,
          label: n.label,
          icon: n.icon,
          color: n.color,
        }))}
        activeKey={activeKey}
        onSelect={(k) => setActiveKey(k as PrepSectionKey)}
        detailLabel={activeItem.label}
        detailLabelColor={activeItem.color}
        height="clamp(480px, 60vh, 720px)"
        detail={
          activeKey === "questions" ? (
            <QuestionList questions={parsed.questions} />
          ) : (
            <div data-testid="prep-section-body">
              <EvaluationMarkdown
                source={parsed.sections[activeKey] ?? ""}
              />
            </div>
          )
        }
      />
    </div>
  );
}
